import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Seo from '../components/Seo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Home = () => {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadContent = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/content/landing-page`);
        setContent(response.data);
      } catch (error) {
        console.error('Failed to load content:', error);
      } finally {
        setLoading(false);
      }
    };
    loadContent();
  }, []);

  // Select a random hero image - useMemo ensures same image during render
  const randomHeroImage = useMemo(() => {
    const images = content?.hero?.hero_images?.filter(img => !!img);
    return images && images.length > 0 ? images[Math.floor(Math.random() * images.length)] : null;
  }, [content]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#002F6C]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Seo
        title="studio 2+2 — landscape architecture practice, Ahmedabad"
        description="studio 2+2 is a landscape architecture practice in Ahmedabad, India. 1000+ projects over nine years. taking care of your natural affairs."
      />
      {/* Hero Section - Full Screen with Minimal Overlay */}
      <section className="relative h-screen">
        {/* Background Image or Color */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={randomHeroImage ? { backgroundImage: `url(${randomHeroImage})` } : { backgroundColor: '#002F6C' }}
        >
          {randomHeroImage ? (
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-transparent"></div>
          ) : (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <span className="text-[20vw] font-bold text-white/5 tracking-widest" style={{ fontFamily: 'Inter, sans-serif' }}>2+2</span>
            </div>
          )}
        </div>
        
        {/* Hero Content - Positioned at Bottom */}
        <div className="relative z-10 h-full flex flex-col justify-end px-4 md:px-8 lg:px-12 pb-24">
          <div className="max-w-3xl space-y-6">
            {/* Tagline - Light and Elegant */}
            <p 
              className="text-white/90 text-lg md:text-xl lowercase tracking-wide"
              style={{ fontFamily: 'Lato, sans-serif', fontWeight: 300 }}
            >
              twoplustwo is a landscape architecture practice in Ahmedabad, India — taking care of your natural affairs.
            </p>
            
            {/* CTA Buttons - More Transparent */}
            <div className="flex flex-wrap gap-4">
              <Link
                to="/work"
                className="inline-block bg-white/90 backdrop-blur-sm text-[#002F6C] px-8 py-3 hover:bg-white transition-all lowercase text-sm"
                style={{ fontFamily: 'Inter, sans-serif', fontWeight: 700 }}
              >
                view our work
              </Link>
              <Link
                to="/studio"
                className="inline-block border border-white/70 text-white/90 px-8 py-3 hover:bg-white/10 hover:border-white transition-all lowercase text-sm backdrop-blur-sm"
                style={{ fontFamily: 'Inter, sans-serif', fontWeight: 700 }}
              >
                about studio
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-5 h-8 border border-white/40 rounded-full flex justify-center pt-1.5">
            <div className="w-1 h-2 bg-white/60 rounded-full"></div>
          </div>
        </div>
      </section>

      {/* Brief Intro */}
      <section className="py-24 md:py-32 px-4 md:px-8 lg:px-12 bg-gray-50">
        <div className="max-w-4xl mx-auto text-center space-y-8">
          <h2 
            className="text-3xl md:text-4xl text-gray-900 lowercase"
            style={{ fontFamily: 'Inter, sans-serif', fontWeight: 700 }}
          >
            designing spaces that matter
          </h2>
          <p 
            className="text-lg leading-relaxed max-w-3xl mx-auto lowercase text-gray-600"
            style={{ fontFamily: 'Lato, sans-serif', fontWeight: 300 }}
          >
            most of what we make begins outside. we're a landscape-led practice, so the garden comes first and the buildings and interiors take their cue from it. our work spans homes, institutions, and experimental projects across india.
          </p>
          <Link
            to="/practice"
            className="inline-block text-[#002F6C] hover:underline lowercase text-sm tracking-wide"
            style={{ fontFamily: 'Inter, sans-serif', fontWeight: 700 }}
          >
            learn about our practice →
          </Link>
        </div>
      </section>

      {/* Minimal Footer */}
      <footer className="py-8 px-4 md:px-8 lg:px-12 text-center" style={{ backgroundColor: '#F8F8F7' }} data-testid="home-footer">
        <p className="text-xs lowercase" style={{ fontFamily: 'Lato, sans-serif', fontWeight: 300, color: '#3A3530' }}>
          taking care of your natural affairs ·{' '}
          <Link
            to="/projects"
            className="hover:underline"
            style={{ color: '#002F6C', fontWeight: 600 }}
            data-testid="footer-all-projects-link"
          >
            all projects
          </Link>
        </p>
      </footer>
    </div>
  );
};

export default Home;
